/**
 * SupportingDocUploader
 *
 * Reusable component for attaching supporting documents to AI draft generation.
 * Extracts text in-memory and never stores files. "Process and forget."
 *
 * Collapsed by default — renders as a single inline toggle until the user
 * clicks it or files are attached. Zero vertical footprint when unused.
 *
 * Props:
 *   documents           - array of { file?, filename, status, extractedText, fileType, byteSize, error? }
 *   onDocumentsChange   - callback with the full updated documents array
 *   maxFiles            - max files allowed (default 2)
 *   disabled            - disables upload during AI generation
 *   acceptTypes         - file input accept string
 */

import { useState, useRef } from 'react';
import { FiPaperclip } from 'react-icons/fi';
import { aiAPI } from '../../services/api';
import { useUI } from '../../context/UIContext';

const ACCEPT_TYPES = '.pdf,.jpg,.jpeg,.png,.txt,.docx,.pptx';
const MAX_FILES = 2;

const FILE_TYPE_ICONS = {
  pdf: '📄',
  image: '🖼️',
  text: '📝',
  docx: '📃',
  error: '⚠️',
  unknown: '📎',
};

const STATUS_BADGES = {
  uploading: { label: 'Extracting...', bg: '#f3ebd7', color: '#8a6a1f', animate: true },
  extracted: { label: 'Ready', bg: '#e3f0ed', color: '#145f5f', animate: false },
  error: { label: 'Failed', bg: '#f2e9e6', color: '#9a564a', animate: false },
};

const formatBytes = (bytes) => {
  if (!bytes || bytes < 1024) return `${bytes || 0} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export default function SupportingDocUploader({
  documents = [],
  onDocumentsChange,
  maxFiles = MAX_FILES,
  disabled = false,
  acceptTypes = ACCEPT_TYPES,
}) {
  const { showToast } = useUI();
  const inputRef = useRef(null);

  // Collapsed by default; always toggleable
  const hasFiles = documents.length > 0;
  const [expanded, setExpanded] = useState(false);

  const readyCount = documents.filter((d) => d.status === 'extracted').length;
  const hasRoom = documents.length < maxFiles;
  const isExtracting = documents.some((d) => d.status === 'uploading');

  /* ── handle file selection ───────────────────────────────────────── */
  const handleFiles = async (event) => {
    const selected = Array.from(event.target.files || []);
    if (inputRef.current) inputRef.current.value = '';
    if (!selected.length) return;

    const slotsLeft = maxFiles - documents.length;
    const filesToAdd = selected.slice(0, slotsLeft);

    if (selected.length > slotsLeft) {
      showToast(`Only ${slotsLeft} more file(s) allowed (max ${maxFiles})`, 'warning');
    }

    // Add files with "uploading" status
    const pending = filesToAdd.map((f) => ({
      filename: f.name,
      status: 'uploading',
      extractedText: '',
      fileType: '',
      byteSize: f.size,
    }));

    // Functional updater so rapid successive selections don't clobber each other.
    onDocumentsChange((prev) => [...prev, ...pending]);
    setExpanded(true);

    // Extract text from all new files in one batch
    try {
      const res = await aiAPI.extractSupportingDocuments(filesToAdd);

      if (res?.success && Array.isArray(res.data?.documents)) {
        const extracted = res.data.documents;
        const results = extracted.map((doc, idx) => ({
          filename: doc.filename || filesToAdd[idx]?.name || 'unknown',
          status: doc.error ? 'error' : 'extracted',
          extractedText: doc.extractedText || '',
          fileType: doc.fileType || '',
          byteSize: doc.byteSize || filesToAdd[idx]?.size || 0,
          error: doc.error || null,
        }));

        // Reconcile against the LATEST state (functional updater) so a concurrent
        // add/remove during extraction isn't silently lost: drop this batch's
        // "uploading" placeholders, then append the extracted results.
        const pendingNames = new Set(pending.map((p) => p.filename));
        onDocumentsChange((prev) => [
          ...prev.filter((d) => !(d.status === 'uploading' && pendingNames.has(d.filename))),
          ...results,
        ]);

        const successCount = extracted.filter((d) => !d.error).length;
        if (successCount > 0) {
          showToast(
            `${successCount} document${successCount > 1 ? 's' : ''} extracted — details will be used in AI draft`,
            'success',
          );
        }
        const failCount = extracted.filter((d) => d.error).length;
        if (failCount > 0) {
          showToast(`${failCount} file${failCount > 1 ? 's' : ''} failed to extract`, 'error');
        }
      } else {
        throw new Error(res?.message || 'Extraction failed');
      }
    } catch (err) {
      // Mark this batch's pending placeholders as failed — functional updater so
      // concurrent add/remove during extraction is preserved.
      const pendingNames = new Set(pending.map((p) => p.filename));
      onDocumentsChange((prev) => [
        ...prev.filter((d) => !(d.status === 'uploading' && pendingNames.has(d.filename))),
        ...filesToAdd.map((f) => ({
          filename: f.name,
          status: 'error',
          extractedText: '',
          fileType: '',
          byteSize: f.size,
          error: err.message || 'Extraction failed',
        })),
      ]);
      showToast(err.message || 'Failed to extract documents', 'error');
    }
  };

  /* ── remove a document ───────────────────────────────────────────── */
  const removeDoc = (idx) => {
    const next = documents.filter((_, i) => i !== idx);
    onDocumentsChange(next);
  };

  /* ── collapsed state: single inline toggle with file count ─────── */
  if (!expanded) {
    return (
      <button
        type="button"
        onClick={() => setExpanded(true)}
        className="w-full flex items-center gap-[6px] px-4 py-[5px] border-b text-left transition-colors hover:bg-[#f0ebe0]"
        style={{ background: '#faf9f6', borderColor: '#ddd6c6' }}
      >
        <FiPaperclip size={11} className="text-[#7a7060]" />
        <span className="text-[11px] text-[#7a7060]">
          {hasFiles ? 'Supporting documents' : 'Attach supporting documents'}
        </span>
        {hasFiles ? (
          <span
            className="text-[9.5px] px-[6px] py-[1px] rounded-full font-bold"
            style={{ background: '#e3f0ed', color: '#145f5f' }}
          >
            {readyCount} attached
          </span>
        ) : (
          <span className="text-[9.5px] text-[#a09880]">(DIN letter, PAN card, etc.)</span>
        )}
        <svg
          width="10" height="10" viewBox="0 0 10 10"
          className="ml-auto shrink-0"
          fill="none" stroke="#7a7060" strokeWidth="1.5"
          strokeLinecap="round" strokeLinejoin="round"
        >
          <path d="M2.5 3.5L5 6.5L7.5 3.5" />
        </svg>
      </button>
    );
  }

  /* ── expanded state ──────────────────────────────────────────────── */
  return (
    <div
      className="px-4 py-[8px] border-b"
      style={{ background: '#faf9f6', borderColor: '#ddd6c6' }}
    >
      {/* Header row */}
      <div className="flex items-center gap-2 mb-[5px]">
        <button
          type="button"
          onClick={() => setExpanded(false)}
          className="flex items-center gap-[6px] text-left"
        >
          <FiPaperclip size={11} className="text-[#3a3020]" />
          <span className="text-[11.5px] font-semibold text-[#3a3020]">
            Supporting Documents
          </span>
        </button>
        {readyCount > 0 && (
          <span
            className="text-[9.5px] px-[6px] py-[1px] rounded-full font-bold"
            style={{ background: '#e3f0ed', color: '#145f5f' }}
          >
            {readyCount} ready
          </span>
        )}
        <span className="ml-auto flex items-center gap-2">
          {/* Upload button inline with header */}
          {hasRoom && (
            <label
              className={`inline-flex items-center gap-1 px-[8px] py-[3px] rounded-lg border text-[10.5px] font-semibold cursor-pointer transition-colors hover:border-[#1e8a8a] ${
                disabled || isExtracting ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              style={{ borderColor: '#ddd6c6', background: '#fff', color: '#3a3020' }}
            >
              {isExtracting ? '⏳ Extracting...' : '+ Attach'}
              <input
                ref={inputRef}
                type="file"
                accept={acceptTypes}
                multiple
                className="hidden"
                onChange={handleFiles}
                disabled={disabled || isExtracting}
              />
            </label>
          )}
          <span className="text-[9.5px] text-[#a09880]">
            {documents.length}/{maxFiles}
          </span>
          <button
            type="button"
            onClick={() => setExpanded(false)}
            className="text-[#7a7060] hover:text-[#3a3020] leading-none transition-colors"
            title="Collapse"
          >
            <svg
              width="10" height="10" viewBox="0 0 10 10"
              fill="none" stroke="currentColor" strokeWidth="1.5"
              strokeLinecap="round" strokeLinejoin="round"
            >
              <path d="M2.5 6.5L5 3.5L7.5 6.5" />
            </svg>
          </button>
        </span>
      </div>

      {/* Hint when no files yet */}
      {!hasFiles && (
        <div className="text-[10px] text-[#a09880] mb-[2px]">
          PDF, Images, TXT, DOCX · max 15 MB each · files not stored; AI usage metadata is logged for monitoring
        </div>
      )}

      {/* File list */}
      {hasFiles && (
        <div className="flex flex-col gap-[3px]">
          {documents.map((doc, idx) => {
            const icon = FILE_TYPE_ICONS[doc.fileType] || FILE_TYPE_ICONS.unknown;
            const badge = STATUS_BADGES[doc.status] || STATUS_BADGES.error;

            return (
              <div
                key={`${doc.filename}-${idx}`}
                className="flex items-center gap-2 px-[8px] py-[4px] rounded-lg border"
                style={{
                  borderColor: doc.status === 'error' ? '#e6d2cb' : '#e8e3d8',
                  background: doc.status === 'error' ? '#f2e9e6' : '#fff',
                }}
              >
                <span className="text-[12px] shrink-0">{icon}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-[10.5px] font-medium text-[#08111f] truncate">
                    {doc.filename}
                  </div>
                  {doc.error && (
                    <div className="text-[9px] text-[#9a564a] truncate">{doc.error}</div>
                  )}
                </div>
                <span className="text-[9px] text-[#7a7060] shrink-0">
                  {formatBytes(doc.byteSize)}
                </span>
                <span
                  className={`text-[8.5px] px-[4px] py-[1px] rounded-full font-bold shrink-0 ${
                    badge.animate ? 'animate-pulse' : ''
                  }`}
                  style={{ background: badge.bg, color: badge.color }}
                >
                  {badge.label}
                </span>
                <button
                  type="button"
                  onClick={() => removeDoc(idx)}
                  className="text-[10px] text-[#7a7060] hover:text-[#9a564a] shrink-0 leading-none"
                  title="Remove"
                  disabled={doc.status === 'uploading'}
                >
                  ✕
                </button>
              </div>
            );
          })}
          {/* Privacy notice */}
          <div className="text-[8.5px] text-[#a09880] flex items-center gap-1 mt-[1px]">
            <span>🔒</span>
            <span>File not stored. AI usage logged for monitoring.</span>
          </div>
        </div>
      )}
    </div>
  );
}
